import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, BellDot, Check, X } from "lucide-react";

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const nav = useNavigate();
  const popRef = useRef(null);

  const load = () => api.get("/notifications").then(r=>{ setItems(r.data.items||[]); setUnread(r.data.unread||0); }).catch(()=>{});

  useEffect(() => {
    load();
    // Real-time-ish: poll every 20s. Also refresh when the tab regains focus, so
    // people who leave Hoomanz open in a background tab see the latest immediately.
    const iv = setInterval(load, 20000);
    const onFocus = () => load();
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const openItem = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(()=>{});
    setOpen(false);
    load();
    if (n.link) nav(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(()=>{});
    load();
  };

  return (
    <div className="relative" ref={popRef}>
      <button onClick={()=>{ setOpen(!open); if (!open) load(); }} className="btn-ghost relative" data-testid="notifications-bell">
        {unread > 0 ? <BellDot size={18}/> : <Bell size={18}/>}
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-[#0057FF] text-white text-[10px] flex items-center justify-center font-medium" data-testid="notifications-unread">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-[360px] card shadow-xl z-50" data-testid="notifications-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E3]">
            <p className="font-heading text-lg">Notifications</p>
            <button onClick={markAll} className="text-xs text-[#4A4A4A] hover:text-[#0A0A0A]" data-testid="notifications-mark-all">
              <Check size={12} className="inline mr-1"/> Mark all read
            </button>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-5 py-12 text-sm text-[#8A8A8A] text-center">No notifications yet.</p>
            ) : items.map(n => (
              <button key={n.id} onClick={()=>openItem(n)}
                className={`w-full text-left px-5 py-4 border-b border-[#F0EFEC] hover:bg-[#F2F2F0] transition-colors ${!n.read ? "bg-[#FAFAFA]" : ""}`}
                data-testid={`notification-${n.id}`}>
                <div className="flex items-start gap-3">
                  {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-[#0057FF] shrink-0"/>}
                  <div className={!n.read ? "" : "ml-5"}>
                    <p className="font-medium text-sm">{n.title}</p>
                    <p className="text-xs text-[#4A4A4A] mt-0.5 line-clamp-2">{n.body}</p>
                    <p className="text-[10px] text-[#8A8A8A] mt-1">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
